import { ErrorDialog, Skeleton } from "@codat/orchard-ui";
import { usePermissionsQuery } from "api/permissions";
import React, { PropsWithChildren, createContext, useContext } from "react";

import Page from "components/Page";

export enum PermissionsStatusType {
  LOADING,
  ERROR,
  READY,
}

type PermissionsStatus =
  | PermissionsStatusType.LOADING
  | PermissionsStatusType.READY
  | PermissionsStatusType.ERROR;

interface PermissionsData {
  data?: string[];
  status: PermissionsStatus;
}

interface CodatError {
  correlationId: string;
  error: string;
  service: string;
  statusCode: number;
}

type PermissionsContextType = PermissionsData;

export const PermissionsContext = createContext<PermissionsContextType>(
  {} as PermissionsContextType
);

interface PermissionsContextProviderProps {
  requiredPermissions: string[] | undefined;
}

export const PermissionsContextProvider: React.FC<
  PropsWithChildren<PermissionsContextProviderProps>
> = ({ requiredPermissions, children }) => {
  const [{ loading, data, error }] = usePermissionsQuery();

  if (loading) {
    return (
      <Page>
        <Skeleton />
      </Page>
    );
  }

  if (error) {
    const codatError = error.response?.data as CodatError;
    const errorMessage = codatError?.error
      ? `An error occured getting permissions: ${codatError?.error}`
      : "An unexpected error occured getting permissions";

    return (
      <ErrorDialog
        statusCode={codatError?.statusCode ?? error.response?.status ?? 500}
        correlationId={codatError?.correlationId}
        message={errorMessage}
      />
    );
  }

  const doesntHavePermissions =
    data &&
    requiredPermissions &&
    !data.some((userPermission) =>
      requiredPermissions.includes(userPermission)
    );
  if (doesntHavePermissions) {
    return (
      <ErrorDialog
        statusCode={401}
        message="You do not have permissions to view this page"
      />
    );
  }

  const getStatus = (): PermissionsStatus => {
    if (error) {
      return PermissionsStatusType.ERROR;
    }

    if (loading) {
      return PermissionsStatusType.LOADING;
    }

    return PermissionsStatusType.READY;
  };

  return (
    <PermissionsContext.Provider
      value={{
        status: getStatus(),
        data,
      }}
    >
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissionsContext = () => useContext(PermissionsContext);
